import React from 'react';
import { CodeSize, BaseSize, TextWeight, TextColor } from '@/shared/types/text';
import { cn } from '@/shared/utils/cn';
import { SYSTEM_COLORS } from './constants';

export interface CodeProps {
  /** Tamaño del code: md o tamaños base */
  size?: CodeSize | BaseSize;
  /** Peso de la fuente (solo aplica si se usa tamaño base) */
  weight?: TextWeight;
  /** Contenido del código */
  children: React.ReactNode;
  /** Clase CSS adicional */
  className?: string;
  /** Elemento HTML a renderizar (por defecto: code) */
  as?: keyof JSX.IntrinsicElements;
  /** Color del texto: puede ser un color del sistema de diseño o un color CSS personalizado */
  color?: TextColor | (string & {});
  /** @deprecated Usa 'color' en su lugar */
  textColor?: TextColor;
}

export const Code = ({
  size = 'md',
  weight = 'regular',
  children,
  className,
  as = 'code',
  color,
  textColor,
  ...props
}: CodeProps) => {
  const Component = as;

  // Construir las clases según el tamaño
  const getTypographyClasses = (): string => {
    const classes: string[] = [];

    if (size === 'md') {
      // Usar clases individuales de Tailwind que están definidas en fontSize, fontWeight, lineHeight, letterSpacing
      classes.push('ui-text-code-md');
      classes.push('ui-font-code-md');
      classes.push('ui-leading-code-md');
      classes.push('ui-tracking-code-md');
    } else if (size) {
      // Usar valores base (2xs, xs, sm, md, lg, xl, 2xl, etc.)
      classes.push(`ui-text-${size}`);

      // Aplicar weight base
      if (weight !== 'regular') {
        classes.push(`ui-font-${weight}`);
      }
    }

    return classes.join(' ');
  };

  const typographyClasses = getTypographyClasses();

  // Verificar si el color es del sistema de diseño
  const isSystemColor = color && SYSTEM_COLORS.includes(color as TextColor);

  // Estilos inline solo para colores CSS personalizados
  const style = color && !isSystemColor ? { color } : undefined;

  // Color del sistema de diseño o por defecto: tertiary
  const getSystemColorClass = (): string => {
    if (isSystemColor) return `ui-text-${color}`;
    if (color) return '';
    if (textColor) return `ui-text-${textColor}`;
    return 'ui-text-tertiary'; // Por defecto
  };

  const systemColorClass = getSystemColorClass();

  return (
    <Component
      className={cn('ui-imj', typographyClasses, systemColorClass, className)}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

Code.displayName = 'Code';
